import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const missingToken = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (pwd !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bg-base)]">
      <div className="flex items-center justify-center p-8 fade-up">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <img src="/logo-talmia-ii.svg" alt="Talmia II" className="w-14 h-14 rounded-xl object-cover" />
            <span className="font-display text-xl font-bold">Talmia<span className="text-orange-400">II</span></span>
          </Link>

          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-2 tracking-tight">Set a new password</h1>
          <p className="text-slate-400 mb-8">Pick something you haven&rsquo;t used elsewhere. Minimum 6 characters.</p>

          {missingToken ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-5" data-testid="reset-missing-token">
              <div className="font-semibold text-red-300 mb-1">No reset token found</div>
              <p className="text-sm text-slate-300">This page needs a valid reset link.
                <Link to="/forgot-password" className="text-orange-400 hover:text-orange-300 ml-1 underline">Request a new one</Link>.
              </p>
            </div>
          ) : done ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5" data-testid="reset-success">
              <div className="font-semibold text-emerald-300 mb-1">Password updated</div>
              <p className="text-sm text-slate-300">Redirecting to sign in…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <label className="text-xs font-medium text-slate-300 uppercase tracking-wider">New password</label>
                <input
                  type="password" required autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)}
                  className="input-dark mt-1.5" placeholder="••••••••" minLength={6} data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-300 uppercase tracking-wider">Confirm new password</label>
                <input
                  type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="input-dark mt-1.5" placeholder="••••••••" minLength={6} data-testid="reset-confirm-input"
                />
              </div>
              {error && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="reset-error">
                  {error}
                </div>
              )}
              <button type="submit" disabled={loading} className="btn-coral w-full mt-2" data-testid="reset-submit">
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          )}

          <p className="mt-6 text-sm text-slate-400">
            Remembered it? <Link to="/login" className="text-orange-400 hover:text-orange-300 font-medium">Back to sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/40 via-slate-900 to-orange-900/30" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 20%, rgba(255,104,32,0.28) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(90,79,207,0.30) 0%, transparent 50%)" }} />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="font-display text-3xl font-bold leading-tight max-w-md">
            Almost there.
          </div>
          <p className="text-slate-400 mt-3 max-w-md">This link works exactly once — after you set the new password, you&rsquo;re straight into Talmia.</p>
        </div>
      </div>
    </div>
  );
}
